import { Box, Typography, CircularProgress, Card, Button } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import RightSideBar, { RightSideBarPinButton } from "./RightSideBar";
import { useBudget } from "../../../contexts/BudgetContext/projectbudgets.context";
import {
  BsFillCaretLeftSquareFill,
  BsFillCaretRightSquareFill,
} from "react-icons/bs";
import ImportModal from "./ImportModal";
import { useProject } from "../../../contexts/project.context";
import { CategoriesSelection } from "./CategoriesSelection/CategoriesSelection";
import { RenderBudgetInfo } from "./ActiveBudget/ActiveBudget";
import { FaThumbtack } from "react-icons/fa";
import { RiArrowLeftFill, RiArrowLeftSFill } from "react-icons/ri";
// import { ReactComponent as Unpinned } from "../../../../assets/styled-icons/unpinned.svg";

const BudgetStep = () => {
  const {
    loadingBudget,
    isLoadingBudgetDetail,
    clearBudget,
    loadBudgetList,
    activeBudget,
    negotiationVersion,
    versionInfo,
    budgetList,
    isBudgetDisabled,
  } = useBudget();
  const { tabActive } = useProject();

  const [collapseSideBar, setCollapseSideBar] = useState(true);
  const [hoveringCollapse, setHoveringCollapse] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [leftSidebarHovering, setLeftSidebarHovering] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const leaveTimer = useRef<number | undefined>(undefined);
  const clearLeaveTimer = () => {
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
  };
  const suppressHoverRef = useRef(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const projectId = searchParams.get("projectId");

  useEffect(() => {
    if (projectId) loadBudgetList(projectId);
    return () => clearBudget();
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ pinned: boolean }>;
      const nextPinned = !!ce.detail?.pinned;
      setPinned(nextPinned);

      if (nextPinned) {
        setHoveringCollapse(false);
        suppressHoverRef.current = true;
      } else {
        setCollapseSideBar(true);
        setHoveringCollapse(true); // fica aberto até sair do overlay
        suppressHoverRef.current = false;
      }
    };
    window.addEventListener("rightSidebarPinned", handler as EventListener);
    return () =>
      window.removeEventListener(
        "rightSidebarPinned",
        handler as EventListener
      );
  }, []);

  // ícone reflete o estado visual atual (expandido/colapsado)
  function renderCollapseOption(isExpanded: boolean) {
    const handleClose = () => {
      setCollapseSideBar(true);
      setHoveringCollapse(false);
      suppressHoverRef.current = true;
    };

    const handleOpen = () => {
      suppressHoverRef.current = false;
      setCollapseSideBar(false);
      setHoveringCollapse(true);
    };

    return isExpanded ? null : (
      <div
        title={pinned ? "Unpin sidebar" : "Pin sidebar"}
        style={{
          height: 680,
          width: 14,
          backgroundColor: pinned ? "#1361A4" : "#536574",
          marginTop: 100,
          cursor: "pointer",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          paddingTop: 40,
        }}
      >
        {pinned ? (
          <RiArrowLeftSFill color="white" size={20} />
        ) : (
          <RiArrowLeftSFill size={20} color="white" />
        )}
      </div>
    );
  }

  const isExpanded = pinned || !collapseSideBar || hoveringCollapse;

  const overlayMode = !pinned;

  const handleIconEnter = () => {
    if (pinned) return;
    if (suppressHoverRef.current) return;
    clearLeaveTimer();
    setHoveringCollapse(true);
  };
  const handleIconLeave = () => {
    if (pinned) return;
    clearLeaveTimer();
    leaveTimer.current = window.setTimeout(() => {
      if (!pinned && collapseSideBar) setHoveringCollapse(false);
      suppressHoverRef.current = false;
    }, 120);
  };

  const handleOverlayEnter = () => {
    if (pinned) return;
    clearLeaveTimer();
    setHoveringCollapse(true);
  };
  const handleOverlayLeave = () => {
    if (pinned) return;
    clearLeaveTimer();
    leaveTimer.current = window.setTimeout(() => {
      setHoveringCollapse(false);
      suppressHoverRef.current = false;
    }, 120);
  };

  const toggleRightSideBarPin = () => {
    window.dispatchEvent(
      new CustomEvent("rightSidebarPinned", { detail: { pinned: !pinned } })
    );
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      bgcolor={"#F9FCFF"}
      flex={1}
      margin={"auto"}
      width={"100%"}
      overflow="hidden"
      borderTop={"1px solid #eee"}
      sx={{ flex: 1, minHeight: 0, height: "100%" }}
    >
      {(loadingBudget || isLoadingBudgetDetail) && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 10,
            bgcolor: "rgba(255, 255, 255, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant={"h6"}>
            {isLoadingBudgetDetail
              ? "Carregando orçamento..."
              : "Carregando..."}
          </Typography>
          <CircularProgress />
        </Box>
      )}

      {showImportModal && (
        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          area={""}
          label={""}
        />
      )}

      <Card
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          minHeight: 0,
          boxShadow: "0px 4px 18px 0px #4B465C1A",
          position: "relative",
        }}
      >
        {/* Left sidebar - scroll interno no componente */}
        <Box
          sx={{
            flexShrink: 0,
            width: !sidebarCollapsed || leftSidebarHovering ? 334 : "auto",
            overflow: "hidden",
            minHeight: 0,
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CategoriesSelection
            onHoverChange={setLeftSidebarHovering}
            onCollapseChange={setSidebarCollapsed}
            disabled={isBudgetDisabled}
          />
        </Box>

        {/* Conteúdo central - scroll próprio (scrollbar escondida por enquanto) */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "auto",
            scrollbarColor: "rgba(0, 0, 0, 0.25) transparent",
            "&::-webkit-scrollbar": { width: 10 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0, 0, 0, 0.25)",
              borderRadius: "5px",
            },
          }}
        >
          <Box
            sx={{
              width: overlayMode
                ? "97%"
                : isExpanded
                ? "calc(100% - 60px)"
                : "96%",
              p: "24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              transition: "width 120ms ease",
            }}
          >
            <RenderBudgetInfo />
          </Box>
        </Box>

        {/* Right sidebar - scroll próprio */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            alignSelf: "stretch",
            justifyContent: overlayMode
              ? "flex-start"
              : isExpanded
              ? "flex-end"
              : "flex-start",
            ml: 1,
            marginLeft: "auto",
            width: overlayMode ? "20px" : isExpanded ? "400px" : "20px",
            zIndex: 999,
            transition: "width 120ms ease",
            cursor: "default",
            pointerEvents: "auto",
            minHeight: 0,
          }}
        >
          {/* Pin visível apenas quando o overlay não está aberto.
                Quando overlayMode && isExpanded, o overlay já exibe seu próprio botão. */}
          {!(overlayMode && isExpanded) && (
            <Box
              sx={{
                flexShrink: 0,
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 0,
              }}
            >
              <RightSideBarPinButton
                pinned={pinned}
                onClick={toggleRightSideBarPin}
                onMouseEnter={handleIconEnter}
                onMouseLeave={handleIconLeave}
              />
            </Box>
          )}

          {!overlayMode && isExpanded && (
            <Box
              zIndex={999}
              display="flex"
              flexDirection="column"
              flex={1}
              minHeight={0}
              minWidth={0}
              bgcolor={"#FFF"}
              sx={{
                border: "1px solid #ddd",
                borderTop: "none",
                p: 1,
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0, 0, 0, 0.25) transparent",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0, 0, 0, 0.25)",
                  borderRadius: "3px",
                },
              }}
            >
              <RightSideBar pinned={pinned} pinOutside />
            </Box>
          )}
        </Box>

        {overlayMode && isExpanded && (
          <Box
            onMouseEnter={handleOverlayEnter}
            onMouseLeave={handleOverlayLeave}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              height: "100%",
              width: 374,
              zIndex: (theme) => theme.zIndex.drawer + 1,
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 0,
              }}
            >
              <RightSideBarPinButton
                pinned={pinned}
                onClick={toggleRightSideBarPin}
              />
            </Box>
            <Box
              sx={{
                width: 360,
                bgcolor: "white",
                borderLeft: "1px solid #ddd",
                boxShadow: 6,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 1,
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(0, 0, 0, 0.25) transparent",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { background: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0, 0, 0, 0.25)",
                    borderRadius: "3px",
                  },
                }}
              >
                <RightSideBar pinned={pinned} pinOutside />
              </Box>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default BudgetStep;
